import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useMediaQuery, useTheme } from "@mui/material";
import MuiIconButton from "@mui/material/IconButton";
import { Grip, X } from "lucide-react";
import { useTranslationApp } from "../../../../lib/i18next";
import { useRebrandBanner } from "../../../../hooks/useRebrandBanner";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { BORDER_RADIUS } from "../../../../utils/constants";
import { CAMPAIGN_APPROVAL_PANEL_TITLE } from "../../../../i18n/keysTranslations";
import {
  deriveApprovalStatusFromReviewers,
  hasMultipleApprovalReviewers,
} from "../../../../components/ContentApproval/approvalReviewersUtils";
import CampaignApprovalCommentsContent from "./CampaignApprovalCommentsContent";
import { getCampaignApprovalPanelCopy } from "./campaignApprovalPanelCopy";

const DRAG_HANDLE_CLASS = "campaign-approval-panel-drag-handle";
const PANEL_WIDTH = 320;
const PANEL_HEIGHT = 480;
const PANEL_MIN_WIDTH = 280;
const PANEL_MIN_HEIGHT = 200;
const PANEL_MARGIN = 16;
const PANEL_CENTER_OFFSET_UP = 48;
const PANEL_Z_INDEX = 2100;

const getDefaultPanelLayout = ({ totalTopOffsetPx, isMobile }) => {
  const panelWidth = isMobile
    ? Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN * 2)
    : PANEL_WIDTH;
  const panelHeight = Math.min(
    PANEL_HEIGHT,
    Math.round(window.innerHeight * 0.7)
  );
  const viewportHeight = window.innerHeight - totalTopOffsetPx;

  const x = Math.max(PANEL_MARGIN, (window.innerWidth - panelWidth) / 2);
  const y = Math.max(
    totalTopOffsetPx + PANEL_MARGIN,
    totalTopOffsetPx +
      (viewportHeight - panelHeight) / 2 -
      PANEL_CENTER_OFFSET_UP
  );

  return {
    x,
    y,
    width: panelWidth,
    height: panelHeight,
  };
};

const CampaignApprovalPanel = ({
  approvalData,
  onChangeSectionSelected,
  onClosePanel = () => {},
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { totalTopOffsetPx } = useRebrandBanner();

  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const layoutRef = useRef(null);

  if (!layoutRef.current) {
    layoutRef.current = getDefaultPanelLayout({ totalTopOffsetPx, isMobile });
  }

  const applyPanelPosition = useCallback((x, y) => {
    const panelEl = panelRef.current;
    const layout = layoutRef.current;
    if (!panelEl || !layout) {
      return;
    }

    const maxX = Math.max(0, window.innerWidth - layout.width);
    const maxY = Math.max(0, window.innerHeight - layout.height);
    const nextX = Math.min(Math.max(0, x), maxX);
    const nextY = Math.min(Math.max(0, y), maxY);

    layout.x = nextX;
    layout.y = nextY;
    panelEl.style.transform = `translate3d(${nextX}px, ${nextY}px, 0)`;
  }, []);

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const panelEl = panelRef.current;
    if (!layout || !panelEl) {
      return undefined;
    }

    panelEl.style.width = `${layout.width}px`;
    panelEl.style.height = `${layout.height}px`;
    applyPanelPosition(layout.x, layout.y);
    return undefined;
  }, [applyPanelPosition]);

  const handleDragPointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }

      applyPanelPosition(
        event.clientX - drag.offsetX,
        event.clientY - drag.offsetY
      );
    },
    [applyPanelPosition]
  );

  const handleDragPointerUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("pointermove", handleDragPointerMove);
    window.removeEventListener("pointerup", handleDragPointerUp);
  }, [handleDragPointerMove]);

  const handleDragPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) {
        return;
      }

      const layout = layoutRef.current;
      if (!layout) {
        return;
      }

      event.preventDefault();
      dragRef.current = {
        offsetX: event.clientX - layout.x,
        offsetY: event.clientY - layout.y,
      };
      window.addEventListener("pointermove", handleDragPointerMove);
      window.addEventListener("pointerup", handleDragPointerUp);
    },
    [handleDragPointerMove, handleDragPointerUp]
  );

  useLayoutEffect(() => {
    return () => {
      window.removeEventListener("pointermove", handleDragPointerMove);
      window.removeEventListener("pointerup", handleDragPointerUp);
    };
  }, [handleDragPointerMove, handleDragPointerUp]);

  const panelTitleKey = useMemo(() => {
    const isMultiReviewer = hasMultipleApprovalReviewers(
      approvalData?.reviewers
    );
    const derivedGlobalStatus = deriveApprovalStatusFromReviewers({
      reviewers: approvalData?.reviewers,
      fallbackStatus: approvalData?.status,
    });

    return getCampaignApprovalPanelCopy({
      isMultiReviewer,
      globalStatus: derivedGlobalStatus,
      copyType: "title",
    });
  }, [approvalData?.reviewers, approvalData?.status]);

  if (!approvalData?.enabled) {
    return null;
  }

  return (
    <Box
      ref={panelRef}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: PANEL_Z_INDEX,
        minWidth: PANEL_MIN_WIDTH,
        minHeight: PANEL_MIN_HEIGHT,
        maxHeight: "70vh",
        willChange: "transform",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: BORDER_RADIUS,
          boxShadow: BOX_SHADOW_THEME,
          bgcolor: "background.paper",
        }}
      >
        <Box
          className={DRAG_HANDLE_CLASS}
          onPointerDown={handleDragPointerDown}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            pt: 1.5,
            pb: 1.5,
            bgcolor: "fields.background",
            cursor: "grab",
            flexShrink: 0,
            userSelect: "none",
            touchAction: "none",
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                color: "#000",
                lineHeight: 0,
              }}
            >
              <Grip size={18} />
            </Box>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.25,
              }}
            >
              {t(panelTitleKey || CAMPAIGN_APPROVAL_PANEL_TITLE)}
            </Typography>
          </Box>

          <MuiIconButton
            aria-label="close"
            onClick={onClosePanel}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              flexShrink: 0,
              alignSelf: "center",
              color: "text.secondary",
              width: 24,
              height: 24,
              p: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                color: "text.primary",
                backgroundColor: "transparent",
              },
            }}
          >
            <X size={18} />
          </MuiIconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            pt: 0,
            pb: 1.5,
            pl: 1.5,
            pr: 1,
          }}
        >
          <CampaignApprovalCommentsContent
            approvalData={approvalData}
            onChangeSectionSelected={onChangeSectionSelected}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignApprovalPanel;
